import type { LeagueData } from "../../schemas.js";
import { computeStandings, record, teamMetrics, weeksOf } from "../../standings.js";
import { gameFor } from "../data.js";
import { href } from "../router.js";
import { backLink, effText, esc, fmt1, missedText } from "../ui.js";
import type { View } from "./view.js";
import { notFoundView } from "./not-found.js";

export function teamView(league: LeagueData, teamId: string): View {
  const team = league.teams.find((t) => t.teamId === teamId);
  if (!team) return notFoundView("That team isn't in this league.");

  const { actual, optimal } = computeStandings(league);
  const rankIn = (rows: typeof actual) => rows.findIndex((r) => r.team.teamId === teamId);
  const aRow = actual[rankIn(actual)]!;
  const oRow = optimal[rankIn(optimal)]!;
  const allMetrics = teamMetrics(league);
  const m = allMetrics.get(teamId)!;

  const games = weeksOf(league)
    .map((wk) => gameFor(league, wk, teamId))
    .filter((g) => g !== undefined)
    .reverse(); // latest first

  const result = (us: number, them: number): string =>
    us > them ? `<b class="w">W</b>` : us < them ? `<b class="l">L</b>` : `<b>T</b>`;

  const rows = games
    .map(({ week, a, b, flips, final }) => {
      const link = href.matchup(week, teamId);
      return `
        <tr class="linked" data-href="${link}">
          <th scope="row" class="team"><a class="team-link" href="${href.team(b.team.teamId)}">${esc(b.team.name)}</a>
            <a class="game-week" href="${link}">Week ${week} · Lineups →</a></th>
          <td class="res">${final ? result(a.actual, b.actual) : ""} ${fmt1(a.actual)}<span class="dash">–</span>${fmt1(b.actual)}</td>
          <td class="res opt-col">${final ? result(a.optimal, b.optimal) : ""} ${fmt1(a.optimal)}<span class="dash">–</span>${fmt1(b.optimal)}${
            flips ? ' <span class="tag">flips</span>' : ""
          }</td>
          <td>${missedText(a.missed)}</td>
        </tr>`;
    })
    .join("");

  // "171.0 optimal +52.5": the best-lineup total and how far short of it the real total fell.
  const optimalSub = (actual: number, optimal: number, color?: string) =>
    `${fmt1(optimal)} optimal ${missedText(optimal - actual, color)}`;

  const tile = (label: string, value: string, sub = "") =>
    `<div class="tile"><span class="tile-label">${label}</span><span class="tile-value">${value}</span>${
      sub ? `<span class="tile-sub">${sub}</span>` : ""
    }</div>`;

  const html = `
    ${backLink(href.home(), "Standings")}
    <section class="page-head">
      <h2 class="page-title">${esc(team.name)}</h2>
      <span class="mgr">${esc(team.manager)}</span>
    </section>
    <section class="tiles">
      ${tile("Record", record(aRow), `#${rankIn(actual) + 1} in standings`)}
      ${tile("Optimal record", record(oRow), `#${rankIn(optimal) + 1} in optimal`)}
      ${tile("Efficiency", effText(m.efficiency, "var(--text)"), "")}
      ${tile("Points for", fmt1(aRow.pointsFor), optimalSub(aRow.pointsFor, oRow.pointsFor, "var(--missed)"))}
      ${tile("Points against", fmt1(aRow.pointsAgainst), optimalSub(aRow.pointsAgainst, oRow.pointsAgainst, "var(--muted)"))}
    </section>
    <section class="card">
      <h2>Games</h2>
      <div class="scroll" tabindex="0" role="region" aria-label="Season games">
        <table class="standings team-games">
          <thead><tr><th scope="col" class="team">Opponent</th><th scope="col">Actual score</th><th scope="col" class="opt-col">Best-lineup score</th><th scope="col">Missed</th></tr></thead>
          <tbody>${rows || `<tr><td colspan="4" class="hint">No games yet.</td></tr>`}</tbody>
        </table>
      </div>
    </section>`;

  return {
    title: team.name,
    html,
    mount(root) {
      // Whole row opens the game; real links inside the row keep their own targets.
      root.querySelectorAll<HTMLElement>("tr[data-href]").forEach((tr) =>
        tr.addEventListener("click", (e) => {
          if ((e.target as HTMLElement).closest("a")) return;
          location.hash = tr.dataset["href"]!;
        }),
      );
    },
  };
}
